<template>
  <div>
    <ImageAreaDialog :value="imageArea" @change="updateImageArea" :dialogVisible="dialogVisible" />
    <bean-source-page
      :type="type"
      :columns="columns"
      :form-columns="formColumns"
      :filter-columns="filterColumns"
      :actions="actions"
      :form="form"
      :title="$t('model.post')"
      resource="posts"
    />
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import { filterColumnsHash, columnsHash, formColumnsHash } from '../admin_configs/posts/columns';
import ImageAreaDialog from '@/components/image-area-dialog';

@Component({
  components: {
    ImageAreaDialog
  }
})
export default class Posts extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}
  imageArea = null
  dialogVisible = false

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  filterColumns() {
    return [
      { prop: 'title_cont' },
      { prop: 'subtitle_cont' },
      {
        prop: 'tags_id_eq',
        label: this.$t('post.tagList'),
        renderCell: {
          component: 'select',
          props: {
            filterable: true,
            xRemotePreload: async () => {
              const data = await this.$request.get('acts_as_taggable_on/tags', { params: { page: 0 } });
              return data.map(item => ({ value: item.id, label: item.name }));
            },
          }
        }
      }
    ].filter(Boolean).map(hash => _.merge(filterColumnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  columns(h, { type }) {
    return [
      { prop: 'id' },
      { prop: 'title' },
      { prop: 'subtitle' },
      type === 'show' && { prop: 'desc' },
      {
        prop: 'tag_list',
        label: this.$t('post.tagList'),
        minWidth: 120,
        renderCell(h, { props: { value } }) {
          return _.map(value, (ele) => (<el-tag style="margin-right: 5px;">{ele}</el-tag>));
        }
      },
      { prop: 'position' },
      { prop: 'image' },
      type === 'show' && { prop: 'avatar' },
      type === 'show' && { prop: 'share_image' },
      type === 'show' && { prop: 'created_at' },
      type === 'show' && { prop: 'updated_at' },
    ].filter(Boolean).map(hash => _.merge(columnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   * @param {Object} params.data - 当前表单数据
   * @param {Function} params.change - 调用此方法同步表单数据 change({ field: 'new value' })
   */
  formColumns() {
    return [
      { prop: 'title' },
      { prop: 'subtitle' },
      {
        prop: 'desc',
        renderCell: {
          component: 'textarea'
        }
      },
      {
        prop: 'tag_list',
        label: this.$t('post.tagList'),
        renderCell: {
          component: 'select',
          style: 'width: 100%',
          props: {
            clearable: true,
            multiple: true,
            filterable: true,
            'default-first-option': true,
            xRemotePreload: async () => {
              const data = await this.$request.get('/acts_as_taggable_on/tags', { params: { page: 0 } })
              return _.map(data, ({ name }) => ({ value: name, label: name }));
            }
          }
        }
      },
      {
        prop: 'image',
        renderCell: {
          hint: `${this.$t('suggestSize')} 375:auto`,
        }
      },
      {
        prop: 'avatar',
        renderCell: {
          hint: `${this.$t('suggestSize')} 1:1`,
        }
      },
      {
        prop: 'share_image',
        renderCell: {
          hint: `${this.$t('suggestSize')} 5:4`,
        }
      },
      { prop: 'position' },
    ].filter(Boolean).map(hash => _.merge(formColumnsHash[hash['prop']], hash))
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove }) {
    return [
      {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'posts.edit', params: { id: row.id } }
        }
      },
      {
        component: 'button',
        props: {
          label: this.$t('hotspot'),
          type: 'primary',
          can: 'posts.update',
        },
        on: {
          click: () => {
            this.imageArea = { ..._.get(row, 'hotspot.image_area', { areas: [], containerWidth: 750 }), image: _.get(row, 'image.url', ''), postId: row.id, hotspotId: _.get(row, 'hotspot.id') };
            this.dialogVisible = true;
          }
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'posts.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  async updateImageArea(value) {
    const { areas, containerWidth } = value;
    const body = {
      hotspot_attributes: {
        id: value.hotspotId,
        image_area: {
          areas: areas,
          containerWidth: containerWidth
        }
      }
    }
    await this.$request.put(`posts/${value.postId}`, body);
    this.$message.success(this.$t('operateSuccess'));
    this.$router.go(0);
  }
}

export const route = [
  {
    path: '/posts',
    name: 'posts.index',
    component: Posts,
    props: {
      type: 'index',
    }
  },
  {
    path: '/posts/new',
    name: 'posts.new',
    component: Posts,
    props: {
      type: 'new',
    }
  },
  {
    path: '/posts/:id',
    name: 'posts.show',
    component: Posts,
    props: {
      type: 'show',
    }
  },
  {
    path: '/posts/:id/edit',
    name: 'posts.edit',
    component: Posts,
    props: {
      type: 'edit'
    }
  }
]
</script>

<style lang="scss" scoped>
  .vadmin-nav-layout {
    .layout-content {
      & > .page-content {
        & > div {
          height: 100%;
        }
      }
    }
  }
</style>
